import {
    analyticsActive,
    analyticsConfigured,
    track,
    trackControl,
    watchAnalytics,
    type AnalyticsEvent,
} from "./analytics";

/**
 * The events a PostHog funnel ends on: a new account, and a purchase back
 * from Stripe Checkout. Both happen as the page opens, before the consent
 * record is read, so one waits up to {@link CONVERSION_WAIT_MS} for
 * analytics to come on and is dropped after that. Nothing is sent while
 * analytics is off, and each is sent at most once.
 */

export type ConversionEvent = Extract<AnalyticsEvent, { name: "signed_up" | "purchase_completed" }>;

/** How long a conversion waits for analytics to come on before it is dropped. */
export const CONVERSION_WAIT_MS = 30_000;

let waiting: { send: () => void; at: number }[] = [];
let unwatch: (() => void) | null = null;

function flush(): void {
    if (!analyticsActive()) return;
    const fresh = waiting.filter(({ at }) => Date.now() - at <= CONVERSION_WAIT_MS);
    waiting = [];
    unwatch?.();
    unwatch = null;
    for (const { send } of fresh) send();
}

function sendOrWait(send: () => void): void {
    if (!analyticsConfigured()) return;
    if (analyticsActive()) {
        send();
        return;
    }
    waiting.push({ send, at: Date.now() });
    unwatch ??= watchAnalytics(flush);
}

/** Sends a conversion now while analytics is on, or once it comes on within the wait. */
export function trackConversion(event: ConversionEvent): void {
    sendOrWait(() => track(event));
}

/** The free tool's funnel: an account made from the tool at `path`, as the sign-in comes back. */
export function trackAccountCreated(path: string): void {
    sendOrWait(() =>
        trackControl("tool_account_created", { source: "IntentOnReturn", target: path }),
    );
}

/** For the tests: as the page loads, with nothing waiting. */
export function resetConversionsForTests(): void {
    unwatch?.();
    unwatch = null;
    waiting = [];
}
